// Ubicación en el repo: src/lib/auth.js
// Helpers de autenticación: login por RUT/email + password, enrolamiento y
// verificación de 2FA (TOTP), registro de login_events, bloqueo de cuenta
// tras intentos fallidos, y bloqueo de pantalla por inactividad (checklist
// secciones 1 y 2).
import { supabase } from './supabaseClient';

const RESOLVE_LOGIN_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/resolve-login`;
const RECORD_FAILURE_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/record-login-failure`;
const REQUEST_RESET_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-password-reset`;
const VERIFY_PASSWORD_FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-current-password`;

/**
 * Paso 1 de login: recibe lo que el usuario tipeó en el único campo
 * "RUT o correo", resuelve el email real usado por Supabase Auth, y de
 * paso chequea si la cuenta está bloqueada por intentos fallidos (rate
 * limiting persistente, migración 0023 — no el límite viejo en memoria).
 */
async function resolveEmail(identifier) {
  const res = await fetch(RESOLVE_LOGIN_FN_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ identifier }),
  });
  if (!res.ok) throw new Error('No se pudo validar el usuario. Intenta nuevamente.');
  const { email, locked } = await res.json();
  return { email, locked: !!locked };
}

// Best-effort: si esto falla, no debe romper el mensaje de error normal de
// login (RUT/contraseña incorrectos) que ya se le muestra al usuario.
async function recordLoginFailure(identifier) {
  try {
    await fetch(RECORD_FAILURE_FN_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ identifier }),
    });
  } catch {
    // silencioso a propósito
  }
}

/**
 * Self-service: pide un correo de acceso (link de tipo recovery) sin
 * necesitar una sesión ni un Admin — pensado para cuando el rate limiting
 * bloqueó la cuenta (ver Login.jsx) y la persona necesita entrar igual.
 * Siempre "resuelve ok" del lado del servidor, exista o no la cuenta.
 */
export async function requestPasswordReset(identifier) {
  const res = await fetch(REQUEST_RESET_FN_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ identifier, redirectTo: `${window.location.origin}/definir-password` }),
  });
  if (!res.ok) throw new Error('No se pudo enviar el correo. Intenta nuevamente en unos minutos.');
}

/**
 * Login completo. `identifier` = RUT o email tal como lo tipeó el usuario.
 * Devuelve { needsMfa } si la cuenta tiene 2FA habilitado y falta el
 * segundo factor — en ese caso llama a `verifyMfaChallenge` con el código.
 * Lanza un error con `code === 'ACCOUNT_LOCKED'` si hay 5+ intentos
 * fallidos recientes — Login.jsx lo distingue del error genérico para
 * ofrecer el correo de acceso en vez de solo "contraseña incorrecta".
 */
export async function login(identifier, password) {
  const { email, locked } = await resolveEmail(identifier);
  if (locked) {
    const err = new Error('Demasiados intentos fallidos. La cuenta quedó bloqueada temporalmente.');
    err.code = 'ACCOUNT_LOCKED';
    throw err;
  }
  if (!email) {
    // Mensaje genérico a propósito: no revela si el RUT existe o no.
    throw new Error('RUT/correo o contraseña incorrectos.');
  }

  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    await recordLoginFailure(identifier);
    throw new Error('RUT/correo o contraseña incorrectos.');
  }

  await logLoginEvent(true);

  const { data: factors } = await supabase.auth.mfa.listFactors();
  const hasVerifiedTotp = factors?.totp?.some((f) => f.status === 'verified');
  if (hasVerifiedTotp) {
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal.currentLevel !== 'aal2') {
      return { needsMfa: true, session: data.session };
    }
  }
  return { needsMfa: false, session: data.session };
}

export async function verifyMfaChallenge(code) {
  const { data: factors } = await supabase.auth.mfa.listFactors();
  const factor = factors?.totp?.find((f) => f.status === 'verified');
  if (!factor) throw new Error('No hay un factor 2FA configurado.');

  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
    factorId: factor.id,
  });
  if (challengeError) throw challengeError;

  const { error: verifyError } = await supabase.auth.mfa.verify({
    factorId: factor.id,
    challengeId: challenge.id,
    code,
  });
  if (verifyError) throw new Error('Código incorrecto.');
}

/**
 * Enrolamiento de 2FA (TOTP). Se usa desde la pantalla de perfil del
 * profesional. `qrCode` es un data URL listo para <img>.
 */
export async function enrollTotp() {
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
  if (error) throw error;
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

export async function confirmTotpEnrollment(factorId, code) {
  const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
  if (challengeError) throw challengeError;
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId: challenge.id, code });
  if (error) throw new Error('Código incorrecto, revisa la hora de tu teléfono e inténtalo de nuevo.');
}

export async function logLoginEvent(success) {
  const { data: userData } = await supabase.auth.getUser();
  await supabase.from('login_events').insert({
    auth_user_id: userData?.user?.id ?? null,
    success,
    user_agent: navigator.userAgent,
    // La IP la agrega Supabase/Netlify a nivel de red si se necesita más
    // adelante (headers x-forwarded-for) vía un Edge Function dedicado;
    // se deja null aquí porque el cliente no puede conocer su IP pública
    // de forma confiable.
  });
}

export async function logout() {
  await supabase.auth.signOut();
}

/**
 * Reingreso de contraseña como paso de confirmación antes de una acción
 * sensible (hoy: forzar eliminación definitiva de un paciente, ver
 * ForceDeletePatientModal.jsx). Devuelve true/false, no lanza si la
 * contraseña es simplemente incorrecta.
 *
 * Deliberadamente NO usa `supabase.auth.signInWithPassword()` directo acá:
 * ese mismo cliente ya tiene la sesión real de la persona en aal2 (2FA
 * verificado, ver meets_mfa_requirement en 0002), y volver a llamar
 * signInWithPassword la reemplazaría por una sesión nueva en aal1,
 * degradando sin querer el estado de 2FA en medio del trabajo. En vez de
 * eso, se delega la verificación a un Edge Function que prueba la
 * contraseña con un cliente aislado en el servidor y nunca toca esta
 * sesión — ver supabase/functions/verify-current-password/index.ts.
 */
export async function verifyCurrentPassword(password) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) throw new Error('Tu sesión expiró, vuelve a iniciar sesión.');

  const res = await fetch(VERIFY_PASSWORD_FN_URL, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error('No se pudo verificar la contraseña. Intenta nuevamente.');
  const { ok } = await res.json();
  return !!ok;
}

/**
 * Bloqueo automático de pantalla tras inactividad (checklist sección 2).
 * Uso: en el componente raíz, `useInactivityLock(() => logout(), 10 * 60_000)`.
 */
export function attachInactivityLock(onTimeout, timeoutMs = 10 * 60_000) {
  let timer = setTimeout(onTimeout, timeoutMs);
  const reset = () => {
    clearTimeout(timer);
    timer = setTimeout(onTimeout, timeoutMs);
  };
  const events = ['mousemove', 'keydown', 'click', 'touchstart'];
  events.forEach((ev) => window.addEventListener(ev, reset));
  return () => {
    clearTimeout(timer);
    events.forEach((ev) => window.removeEventListener(ev, reset));
  };
}
